export interface MerchantTransactionAmountLike {
    exchange_rate?: number | null;
    amount_usdt_before_fees?: number | null;
    merchant_commission_usdt?: number | null;
    order_type?: string;
    amount?: number | null;
    currency?: string;
    payer_amount?: number | null;
    payer_currency?: string;
    receiver_amount?: number | null;
    receiver_currency?: string;
    merchant_commission_rate?: number | null;
    merchant_commission_amount?: number | null;
    merchant_credited_usdt?: number | null;
    merchant_reserved_usdt?: number | null;
}

const validAmount = (value?: number | null): number | undefined => {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

export const getMerchantTransactionType = (
    payment: MerchantTransactionAmountLike
): 'deposit' | 'withdraw' | undefined => {
    if (payment.order_type === 'deposit' || payment.order_type === 'withdraw') {
        return payment.order_type;
    }

    if (payment.payer_currency === 'USDT' && payment.receiver_currency !== 'USDT') {
        return 'withdraw';
    }

    if (payment.payer_currency !== 'USDT' && payment.receiver_currency === 'USDT') {
        return 'deposit';
    }

    return undefined;
};

export const getTransactionGrossAmount = (
    payment: MerchantTransactionAmountLike
): number | undefined => {
    const transactionType = getMerchantTransactionType(payment);

    if (transactionType === 'withdraw') {
        return validAmount(payment.receiver_amount) ?? validAmount(payment.amount);
    }

    if (transactionType === 'deposit') {
        return validAmount(payment.payer_amount) ?? validAmount(payment.amount);
    }

    if (payment.payer_currency && payment.payer_currency !== 'USDT') {
        return validAmount(payment.payer_amount) ?? validAmount(payment.amount);
    }

    if (payment.receiver_currency && payment.receiver_currency !== 'USDT') {
        return validAmount(payment.receiver_amount) ?? validAmount(payment.amount);
    }

    return validAmount(payment.amount);
};

export const getTransactionGrossCurrency = (
    payment: MerchantTransactionAmountLike
): string => {
    const transactionType = getMerchantTransactionType(payment);

    if (transactionType === 'withdraw') {
        return payment.receiver_currency || payment.currency || 'RUB';
    }

    if (transactionType === 'deposit') {
        return payment.payer_currency || payment.currency || 'RUB';
    }

    if (payment.payer_currency && payment.payer_currency !== 'USDT') {
        return payment.payer_currency;
    }

    if (payment.receiver_currency && payment.receiver_currency !== 'USDT') {
        return payment.receiver_currency;
    }

    return payment.currency || 'RUB';
};

export const getTransactionUsdtAmount = (
    payment: MerchantTransactionAmountLike
): number | undefined => {
    const transactionType = getMerchantTransactionType(payment);

    if (transactionType === 'deposit') {
        const creditedUsdt = validAmount(payment.merchant_credited_usdt);
        if (creditedUsdt !== undefined) {
            return creditedUsdt;
        }
    }

    if (transactionType === 'withdraw') {
        const reservedUsdt = validAmount(payment.merchant_reserved_usdt);
        if (reservedUsdt !== undefined) {
            return reservedUsdt;
        }
    }

    if (payment.receiver_currency === 'USDT') {
        return validAmount(payment.receiver_amount);
    }

    if (payment.payer_currency === 'USDT') {
        return validAmount(payment.payer_amount);
    }

    return undefined;
};

export const getMerchantCommissionAmountRub = (
    payment: MerchantTransactionAmountLike
): number | undefined => {
    const storedCommission = validAmount(payment.merchant_commission_amount);
    if (storedCommission !== undefined) {
        return storedCommission;
    }

    const commissionRate = validAmount(payment.merchant_commission_rate);
    const grossAmount = getTransactionGrossAmount(payment);
    if (commissionRate === undefined || grossAmount === undefined) {
        return undefined;
    }

    return grossAmount * commissionRate / 100;
};

export const getDepositNetAmountRub = (
    payment: MerchantTransactionAmountLike
): number | undefined => {
    if (getMerchantTransactionType(payment) !== 'deposit') {
        return undefined;
    }

    const grossAmount = getTransactionGrossAmount(payment);
    const commissionAmount = getMerchantCommissionAmountRub(payment);
    if (grossAmount === undefined || commissionAmount === undefined) {
        return undefined;
    }

    return grossAmount - commissionAmount;
};

export const getWithdrawAmountWithCommissionRub = (
    payment: MerchantTransactionAmountLike
): number | undefined => {
    if (getMerchantTransactionType(payment) !== 'withdraw') {
        return undefined;
    }

    const grossAmount = getTransactionGrossAmount(payment);
    const commissionAmount = getMerchantCommissionAmountRub(payment);
    if (grossAmount === undefined || commissionAmount === undefined) {
        return undefined;
    }

    return grossAmount + commissionAmount;
};

export const getMerchantAvailableBalance = (merchant: {
    deposit_balance?: number; withdraw_balance?: number; settlement_balance?: number;
    recalculation_reserved_usdt?: number; available_balance_usdt?: number;
    settlement_rounding_adjustment_usdt?: number;
}): number => {
    const balance = merchant.available_balance_usdt ?? Number(((merchant.deposit_balance || 0)
        - (merchant.withdraw_balance || 0) - (merchant.settlement_balance || 0) - (merchant.recalculation_reserved_usdt || 0)
        + (merchant.settlement_rounding_adjustment_usdt || 0)).toFixed(6));
    return balance === 0 ? 0 : balance;
};
